import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieConsentContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

// Update Google Consent Mode based on user preferences
const updateGoogleConsent = (preferences) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_storage': preferences.marketing ? 'granted' : 'denied',
      'ad_user_data': preferences.marketing ? 'granted' : 'denied',
      'ad_personalization': preferences.marketing ? 'granted' : 'denied',
    });
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie-consent');
    const savedPreferences = localStorage.getItem('cookie-preferences');
    
    if (savedConsent) {
      const consentData = JSON.parse(savedConsent);
      setConsentGiven(consentData.given);
      setShowBanner(!consentData.given);

      if (savedPreferences) {
        const parsedPreferences = JSON.parse(savedPreferences);
        setPreferences(parsedPreferences);
        // Update Google Consent Mode on page load
        updateGoogleConsent(parsedPreferences);
      }
    } else {
      // Show banner if no consent has been given
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    
    setPreferences(allPreferences);
    setConsentGiven(true);
    setShowBanner(false);
    
    localStorage.setItem('cookie-consent', JSON.stringify({
      given: true,
      timestamp: new Date().toISOString()
    }));
    localStorage.setItem('cookie-preferences', JSON.stringify(allPreferences));

    // Initialize Google Analytics dynamically with full consent mode
    updateGoogleConsent(allPreferences);

    // Try to play video directly from user gesture (Firefox requires this)
    // This runs in the same call stack as the click, so user activation is valid
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      if (video.paused && video.muted) {
        video.play().catch(() => {});
      }
    });
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    
    setPreferences(necessaryOnly);
    setConsentGiven(true);
    setShowBanner(false);
    
    localStorage.setItem('cookie-consent', JSON.stringify({
      given: true,
      timestamp: new Date().toISOString()
    }));
    localStorage.setItem('cookie-preferences', JSON.stringify(necessaryOnly));

    // Update Google Consent Mode (deny non-necessary)
    updateGoogleConsent(necessaryOnly);
  };

  const updatePreferences = (newPreferences) => {
    const updatedPreferences = { ...preferences, ...newPreferences };
    setPreferences(updatedPreferences);
    
    localStorage.setItem('cookie-preferences', JSON.stringify(updatedPreferences));
    localStorage.setItem('cookie-consent', JSON.stringify({ 
      given: true, 
      timestamp: new Date().toISOString() 
    }));
    
    setConsentGiven(true);
    setShowBanner(false);

    // Update Google Consent Mode
    updateGoogleConsent(updatedPreferences);
  };

  const resetConsent = () => {
    localStorage.removeItem('cookie-consent');
    localStorage.removeItem('cookie-preferences');
    setConsentGiven(false);
    setShowBanner(true);
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    });
  };

  const value = {
    consentGiven,
    showBanner,
    preferences,
    acceptAll,
    acceptNecessaryOnly,
    updatePreferences,
    resetConsent,
    setShowBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};